import { useState, useEffect, useCallback, useRef } from 'react'
import clsx from 'clsx'
import { Head, Link } from '@inertiajs/react'
import Button from '@/components/ui/Button'
import AppLayout from '@/layouts/AppLayout'
import Notification from '@/components/ui/Notification'
import PageHeader from '@/components/ui/PageHeader'
import DashboardTable from './DashboardTable'
import Toolbar from './Toolbar'
import FiltersPresetsModal from './FiltersPresetsModal'
import ColumnChooserModal from './ColumnChooserModal'
import { DEFAULT_HIDDEN_COLUMNS } from './columns'
import { ShoppingBagIcon } from '@heroicons/react/24/outline'
import { usePermissions } from '@/hooks/usePermissions'
import {
  LEGACY_KEYS,
  readExcludeShipped, writeExcludeShipped,
  readExcludeClosedLost, writeExcludeClosedLost,
  readShowShippedLast90, writeShowShippedLast90,
  readExcludeParentGmv, writeExcludeParentGmv,
  readHiddenColumnNames, writeHiddenColumnNames,
  readColumnOrder, readColumnWidths, writeColumnOrder, writeColumnWidths,
  readFilters, writeFilters,
  clearAllDashboardSettings,
} from './dashboardStorage'

// ─── Types ──────────────────────────────────────

interface FilterPreset {
  id: number
  name: string
  setting: Record<string, unknown>
}

interface DashboardProps {
  filtersPresets: FilterPreset[]
  subStateNames: Record<string, Record<string, string>>
  subStateComments: Record<string, Record<string, string>>
  statesOptions: { label: string; value: string }[]
  probabilitiesOptions: { label: string; value: string }[]
  financialStagesOptions: { label: string; value: string }[]
}

// ─── localStorage helpers ───────────────────────

function lsGet<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key)
    return v ? JSON.parse(v) : fallback
  } catch { return fallback }
}
function lsSet(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)) } catch { /* noop */ }
}

// ─── Component ──────────────────────────────────

export default function DashboardIndex({
  filtersPresets, subStateNames, subStateComments,
  statesOptions, probabilitiesOptions, financialStagesOptions,
}: DashboardProps) {
  // ─── Notification ───────────────────────────
  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)
  const notify = useCallback((msg: string) => {
    setNotifMessage(msg)
    setShowNotif(true)
  }, [])

  // ─── CSRF ───────────────────────────────────
  const csrfToken = typeof document !== 'undefined'
    ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    : ''

  // ─── Current user role (from Inertia shared props) ─
  const currentUserRole = (() => {
    try {
      const el = document.getElementById('app')
      const page = el?.dataset?.page ? JSON.parse(el.dataset.page) : null
      return page?.props?.currentUser?.role || ''
    } catch { return '' }
  })()

  // ─── Data state ─────────────────────────────
  const [data, setData] = useState<Record<string, unknown>[]>([])
  const [users, setUsers] = useState<{ label: string; value: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [presets, setPresets] = useState<FilterPreset[]>(filtersPresets)
  const [presetsOpen, setPresetsOpen] = useState(false)
  const [columnChooserOpen, setColumnChooserOpen] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [fullscreenExiting, setFullscreenExiting] = useState(false)
  const toggleFullscreen = useCallback(() => {
    if (fullscreen) {
      setFullscreenExiting(true)
      setTimeout(() => { setFullscreen(false); setFullscreenExiting(false) }, 250)
    } else {
      setFullscreen(true)
    }
  }, [fullscreen])

  // ─── Global filters ─────────────────────────
  // All four read/write via dashboardStorage so the legacy app can
  // see — and tweak — the same values.
  const [excludeShipped, setExcludeShipped] = useState(() => readExcludeShipped())
  const [excludeClosedLost, setExcludeClosedLost] = useState(() => readExcludeClosedLost())
  const [showShippedLast90, setShowShippedLast90] = useState(() => readShowShippedLast90())
  const [excludeParentClosedGmv, setExcludeParentClosedGmv] = useState(() => readExcludeParentGmv())

  // Persist global filters (legacy boolean keys)
  useEffect(() => { writeExcludeShipped(excludeShipped) }, [excludeShipped])
  useEffect(() => { writeExcludeClosedLost(excludeClosedLost) }, [excludeClosedLost])
  useEffect(() => { writeShowShippedLast90(showShippedLast90) }, [showShippedLast90])
  useEffect(() => { writeExcludeParentGmv(excludeParentClosedGmv) }, [excludeParentClosedGmv])

  // ─── Column visibility ─────────────────────
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => {
    const saved = readHiddenColumnNames([])
    return new Set(saved.length > 0 ? saved : DEFAULT_HIDDEN_COLUMNS)
  })
  const handleHiddenColumnsChange = useCallback((hidden: Set<string>) => {
    setHiddenColumns(hidden)
    writeHiddenColumnNames([...hidden])
  }, [])

  // ─── Selection ──────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const selectedCount = selectedIds.size
  const { can } = usePermissions()

  // ─── Fetch orders ───────────────────────────
  const fetchOrders = useCallback(async () => {
    setLoading(true)
    const hiddenArr = [...hiddenColumns]
    const params = new URLSearchParams({
      exclude_shipped_to_client_orders: String(excludeShipped),
      exclude_closed_lost_orders: String(excludeClosedLost),
      show_shipped_last_90_days_orders: String(showShippedLast90),
      exclude_parent_with_close_gmv: String(excludeParentClosedGmv),
      hidden_columns: hiddenArr.join(','),
    })
    try {
      const res = await fetch(`/dashboard/orders.json?${params}`)
      if (res.ok) {
        const json = await res.json()
        setData(json.data || [])
        setUsers((json.users || []).map((u: { id: number; name: string; email: string }) => ({
          value: String(u.id),
          label: u.name || u.email,
        })))
      }
    } catch { /* noop */ }
    setLoading(false)
  }, [excludeShipped, excludeClosedLost, showShippedLast90, excludeParentClosedGmv, hiddenColumns])

  // Initial fetch + refetch on global filter change
  const initialFetch = useRef(false)
  useEffect(() => {
    if (!initialFetch.current) {
      initialFetch.current = true
      fetchOrders()
    }
  }, [fetchOrders])

  // Refetch when global filters change (skip initial)
  const prevFilters = useRef({ excludeShipped, excludeClosedLost, showShippedLast90, excludeParentClosedGmv })
  useEffect(() => {
    const prev = prevFilters.current
    if (
      prev.excludeShipped !== excludeShipped ||
      prev.excludeClosedLost !== excludeClosedLost ||
      prev.showShippedLast90 !== showShippedLast90 ||
      prev.excludeParentClosedGmv !== excludeParentClosedGmv
    ) {
      prevFilters.current = { excludeShipped, excludeClosedLost, showShippedLast90, excludeParentClosedGmv }
      fetchOrders()
    }
  }, [excludeShipped, excludeClosedLost, showShippedLast90, excludeParentClosedGmv, fetchOrders])

  // ─── Lazy column fetch ──────────────────────
  const fetchColumn = useCallback(async (columnName: string) => {
    const res = await fetch(`/dashboard/column.json?column=${columnName}`)
    if (res.ok) {
      const json = await res.json()
      return json.data || []
    }
    return []
  }, [])

  // ─── Toolbar actions ────────────────────────
  const clearSettings = useCallback(() => {
    clearAllDashboardSettings()
    window.location.reload() // only allowed reset — full state wipe
  }, [])

  const clearFilters = useCallback(() => {
    writeFilters({})
    setData(prev => [...prev]) // trigger re-render
    notify('Saved:Filters cleared')
  }, [notify])

  const exportExcel = useCallback(async (mode: 'all' | 'selected') => {
    const rows = mode === 'selected'
      ? data.filter(r => selectedIds.has(r.id as number))
      : data
    if (rows.length === 0) return
    try {
      const XLSX = await import('xlsx')
      // json_to_sheet can't serialize arrays/objects — flatten them so array
      // columns like brands_name, tags_name, child_ids appear in the file.
      const flatRows = rows.map(row => {
        const out: Record<string, unknown> = {}
        for (const [k, v] of Object.entries(row)) {
          if (Array.isArray(v)) out[k] = v.join(', ')
          else if (v && typeof v === 'object') out[k] = JSON.stringify(v)
          else out[k] = v
        }
        return out
      })
      const ws = XLSX.utils.json_to_sheet(flatRows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Orders')
      const suffix = mode === 'selected' ? '_selected' : ''
      XLSX.writeFile(wb, `dashboard_export${suffix}_${new Date().toISOString().slice(0, 10)}.xlsx`)
      notify(`Saved:Exported ${rows.length} orders`)
    } catch {
      notify('error:Error:Export failed')
    }
  }, [data, selectedIds, notify])

  // ─── Presets ────────────────────────────────
  const savePreset = useCallback(async (name: string) => {
    const preset = {
      filters: lsGet('v2-dashboard-filters', {}),
      hiddenColumnNames: [...hiddenColumns],
      // Reading the raw legacy LS payloads — preset shape stays
      // identical to what the legacy app saves to the backend.
      columnWidths: (() => { try { return JSON.parse(localStorage.getItem(LEGACY_KEYS.columnWidths) || '[]') } catch { return [] } })(),
      columnOrder:  (() => { try { return JSON.parse(localStorage.getItem(LEGACY_KEYS.columnOrder)  || '[]') } catch { return [] } })(),
      excludeShipped, excludeClosedLost, showShippedLast90, excludeParentClosedGmv,
    }
    const res = await fetch('/dashboard/filters_presets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ name, preset }),
    })
    if (res.ok) {
      setPresets(prev => [...prev, { id: Date.now(), name, setting: preset }])
      notify('Saved:Preset saved')
    }
  }, [hiddenColumns, excludeShipped, excludeClosedLost, showShippedLast90, excludeParentClosedGmv, csrfToken, notify])

  const loadPreset = useCallback((preset: FilterPreset) => {
    const s = preset.setting as Record<string, unknown>
    // Write the legacy payload back verbatim — the legacy app and ours
    // agree on shape thanks to dashboardStorage, so no conversion
    // needed at this boundary either.
    if (s.filters)             localStorage.setItem(LEGACY_KEYS.filters,           JSON.stringify(s.filters))
    if (s.hiddenColumnNames) {
      localStorage.setItem(LEGACY_KEYS.hiddenColumnNames, JSON.stringify(s.hiddenColumnNames))
      setHiddenColumns(new Set(s.hiddenColumnNames as string[]))
    }
    if (s.columnWidths)        localStorage.setItem(LEGACY_KEYS.columnWidths,      JSON.stringify(s.columnWidths))
    if (s.columnOrder)         localStorage.setItem(LEGACY_KEYS.columnOrder,       JSON.stringify(s.columnOrder))
    if (s.excludeShipped != null) setExcludeShipped(s.excludeShipped as boolean)
    if (s.excludeClosedLost != null) setExcludeClosedLost(s.excludeClosedLost as boolean)
    if (s.showShippedLast90 != null) setShowShippedLast90(s.showShippedLast90 as boolean)
    if (s.excludeParentClosedGmv != null) setExcludeParentClosedGmv(s.excludeParentClosedGmv as boolean)
    setPresetsOpen(false)
    fetchOrders()
    notify('Saved:Preset loaded')
  }, [fetchOrders, notify])

  const deletePreset = useCallback(async (name: string) => {
    const res = await fetch('/dashboard/filters_presets', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ name }),
    })
    if (res.ok) {
      setPresets(prev => prev.filter(p => p.name !== name))
      notify('Deleted:Preset removed')
    }
  }, [csrfToken, notify])

  // ─── Render ─────────────────────────────────
  return (
    <>
      <Head title="Customer Orders" />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />

      {!fullscreen && (
        <PageHeader title="Customer Orders" subtitle="Dashboard" icon={<ShoppingBagIcon className="h-5 w-5" />} actions={
          <Link href="/inertia_customer_orders/new">
            <Button>+ New Order</Button>
          </Link>
        } />
      )}

      <div className={clsx(
        'flex flex-col',
        fullscreen
          ? clsx('fixed inset-0 z-[200] bg-white dark:bg-zinc-900', fullscreenExiting ? 'animate-[fullscreen-out_250ms_ease-in_forwards]' : 'animate-[fullscreen-in_300ms_ease-out]')
          : '-mb-4 h-[calc(100dvh-11rem)] lg:-mb-6 lg:h-[calc(100dvh-8rem)]',
      )}>
      <Toolbar
        excludeShipped={excludeShipped}
        onExcludeShippedChange={setExcludeShipped}
        excludeClosedLost={excludeClosedLost}
        onExcludeClosedLostChange={setExcludeClosedLost}
        showShippedLast90={showShippedLast90}
        onShowShippedLast90Change={setShowShippedLast90}
        excludeParentClosedGmv={excludeParentClosedGmv}
        onExcludeParentClosedGmvChange={setExcludeParentClosedGmv}
        onClearSettings={clearSettings}
        onClearFilters={clearFilters}
        onOpenPresets={() => setPresetsOpen(true)}
        onExportExcel={exportExcel}
        onOpenColumnChooser={() => setColumnChooserOpen(true)}
        fullscreen={fullscreen}
        onToggleFullscreen={toggleFullscreen}
        rowCount={data.length}
        selectedCount={selectedCount}
        canShowInVpo={can('Menu:po_search')}
        onShowInVpo={() => {
          // Mirrors legacy DashboardTable.jsx onShowInVpoButtonClick:
          // open /vpo/new with the selected order IDs in a new tab.
          // The Vpo#new controller reads `orders_to_show` and seeds
          // the new VPO's data_source table with those orders' products.
          const ids = Array.from(selectedIds)
          window.open(`/vpo/new?orders_to_show=${encodeURIComponent(JSON.stringify(ids))}`, '_blank')
        }}
      />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
        </div>
      ) : (
        <DashboardTable
          data={data}
          users={users}
          csrfToken={csrfToken}
          onNotify={notify}
          onFetchColumn={fetchColumn}
          statesOptions={statesOptions}
          subStateNames={subStateNames}
          subStateComments={subStateComments}
          probabilitiesOptions={probabilitiesOptions}
          financialStagesOptions={financialStagesOptions}
          currentUserRole={currentUserRole}
          hiddenColumns={hiddenColumns}
          onHiddenColumnsChange={handleHiddenColumnsChange}
          onSelectionChange={setSelectedIds}
        />
      )}
      </div>

      <FiltersPresetsModal
        open={presetsOpen}
        onClose={() => setPresetsOpen(false)}
        presets={presets}
        csrfToken={csrfToken}
        onSave={savePreset}
        onLoad={loadPreset}
        onDelete={deletePreset}
      />

      <ColumnChooserModal
        open={columnChooserOpen}
        onClose={() => setColumnChooserOpen(false)}
        hiddenColumns={hiddenColumns}
        onHiddenColumnsChange={handleHiddenColumnsChange}
      />
    </>
  )
}

DashboardIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
